<template>
  <section
    v-resize="onResize"
    class="contacts-table-wrap flex flex-1 flex-col overflow-hidden"
  >
    <table-v2
      flush
      :is-r-t-l="isRTLView"
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-items="totalItems"
      :page-size="pageSize"
      :loading="isLoading"
      @page-change="$emit('page-change', $event)"
    >
      <template #header>
        <table-header-cell-v2
          v-for="col in columns"
          :key="col.key"
          :title="col.title"
          :show-menu="col.showMenu !== false"
          :is-dropdown-open="activeDropdownColumn === col.key"
          :filtered="isColumnFiltered(col.key)"
          :width="col.width"
          :align="columnAlign"
          @toggle-dropdown="toggleDropdown(col.key)"
          @close-dropdown="closeDropdown"
        >
          <template #dropdown>
            <table-dropdown-v2
              :style="col.menuWidth && { minWidth: `${col.menuWidth}px` }"
              :show-filter="!isDateColumn(col.key)"
              :filter-label="
                $t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.FILTER_BY')
              "
              :sort-label="$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.SORT_BY')"
              :search-placeholder="
                $t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.SEARCH_PLACEHOLDER')
              "
              :filter-query="getColumnQuery(col.key)"
              :show-sort="getShowSort(col.key)"
              :sort-options="getSortOptions(col.key)"
              :current-sort="getCurrentSort(col.key)"
              :filter-options="getFilterOptions(col.key)"
              :selected-filters="getSelectedFilters(col.key)"
              @update:filterQuery="
                value => emitColumnFilter(col.key, { value })
              "
              @sort="order => applySortFromDropdown(col.key, order)"
              @toggle-filter="
                presence => emitColumnFilter(col.key, { presence })
              "
            />
          </template>
        </table-header-cell-v2>
      </template>
      <template #body>
        <table-row-v2
          v-for="contact in tableData"
          :key="contact.id"
          :clickable="true"
          @click="onClickContact(contact.id)"
        >
          <table-cell-v2 :align="columnAlign" class="contacts-name-td">
            <div class="row--user-block">
              <div class="avatar-badge">
                {{ getInitials(contact.name) }}
              </div>
              <span class="user-name" dir="auto">{{
                contact.name || '---'
              }}</span>
            </div>
          </table-cell-v2>
          <table-cell-v2 :align="columnAlign">
            <span dir="ltr">{{ contact.phone_number }}</span>
          </table-cell-v2>
          <table-cell-v2 :align="columnAlign">
            <span class="block truncate" dir="auto">{{ contact.company }}</span>
          </table-cell-v2>
          <table-cell-v2 :align="columnAlign">
            {{ contact.last_activity_at }}
          </table-cell-v2>
          <table-cell-v2 :align="columnAlign">
            <a
              v-if="contact.email"
              v-tooltip.top="{
                content: contact.email,
                classes: ['!max-w-[90vw]', 'break-all'],
              }"
              target="_blank"
              rel="noopener noreferrer nofollow"
              :href="`mailto:${contact.email}`"
              class="contact-email-link"
              dir="ltr"
              @click.stop
            >
              {{ contact.email }}
            </a>
            <span v-else>---</span>
          </table-cell-v2>
          <table-cell-v2 :align="columnAlign">
            {{ contact.created_at }}
          </table-cell-v2>
        </table-row-v2>
      </template>
    </table-v2>

    <empty-state
      v-if="showSearchEmptyState"
      :title="$t('CONTACTS_PAGE.LIST.404')"
    />
    <empty-state
      v-else-if="!isLoading && !contacts.length"
      :title="$t('CONTACTS_PAGE.LIST.NO_CONTACTS')"
    />
    <div
      v-if="isLoading"
      class="flex items-center justify-center text-base min-h-[180px]"
    >
      <spinner />
      <span>{{ $t('CONTACTS_PAGE.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import timeMixin from 'dashboard/mixins/time';
import rtlMixin from 'shared/mixins/rtlMixin';
import TableV2 from 'v2/components/Table/TableV2.vue';
import TableHeaderCellV2 from 'v2/components/Table/TableHeaderCellV2.vue';
import TableDropdownV2 from 'v2/components/Table/TableDropdownV2.vue';
import TableRowV2 from 'v2/components/Table/TableRowV2.vue';
import TableCellV2 from 'v2/components/Table/TableCellV2.vue';
import { buildColumnFilterItems } from '../contactFilterItems';

const COLUMN_WIDTHS = {
  name: 200,
  phone_number: 200,
  company: 200,
  last_activity_at: 160,
  created_at: 160,
};
const FIXED_COLUMNS_WIDTH = Object.values(COLUMN_WIDTHS).reduce(
  (sum, width) => sum + width,
  0
);
const EMAIL_MIN_WIDTH = 180;

export default {
  components: {
    EmptyState,
    Spinner,
    TableV2,
    TableHeaderCellV2,
    TableDropdownV2,
    TableRowV2,
    TableCellV2,
  },
  mixins: [timeMixin, rtlMixin],
  props: {
    contacts: {
      type: Array,
      default: () => [],
    },
    showSearchEmptyState: {
      type: Boolean,
      default: false,
    },
    onClickContact: {
      type: Function,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    activeContactId: {
      type: [String, Number],
      default: '',
    },
    sortParam: {
      type: String,
      default: 'last_activity_at',
    },
    sortOrder: {
      type: String,
      default: 'desc',
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 0,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    columnFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      sortConfig: {},
      activeDropdownColumn: null,
      tableWidth: 0,
    };
  },
  computed: {
    columnAlign() {
      return this.isRTLView ? 'right' : 'left';
    },
    widthScale() {
      if (!this.tableWidth) return 1;
      return Math.min(
        1,
        (this.tableWidth - EMAIL_MIN_WIDTH) / FIXED_COLUMNS_WIDTH
      );
    },
    columns() {
      return [
        {
          key: 'name',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME'),
          width: this.columnWidth('name'),
        },
        {
          key: 'phone_number',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER'),
          width: this.columnWidth('phone_number'),
          menuWidth: 226,
        },
        {
          key: 'company',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.COMPANY'),
          width: this.columnWidth('company'),
        },
        {
          key: 'last_activity_at',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.LAST_ACTIVITY'),
          width: this.columnWidth('last_activity_at'),
          menuWidth: 153,
        },
        {
          key: 'email',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.EMAIL_ADDRESS'),
          menuWidth: 206,
        },
        {
          key: 'created_at',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.CREATED_AT'),
          width: this.columnWidth('created_at'),
          menuWidth: 153,
        },
      ];
    },
    tableData() {
      if (this.isLoading) {
        return [];
      }
      return this.contacts.map(item => {
        // Note: The attributes used here is in snake case
        // as it simplier the sort attribute calculation
        const additional = item?.additional_attributes || {};
        const { last_activity_at: lastActivityAt } = item;
        const { created_at: createdAt } = item;
        return {
          ...item,
          phone_number: item.phone_number || '---',
          company: additional.company_name || '---',
          last_activity_at: lastActivityAt
            ? this.dynamicTime(lastActivityAt)
            : '---',
          created_at: createdAt ? this.dynamicTime(createdAt) : '---',
        };
      });
    },
  },
  watch: {
    sortOrder() {
      this.setSortConfig();
    },
    sortParam() {
      this.setSortConfig();
    },
  },
  mounted() {
    this.setSortConfig();
  },
  methods: {
    onResize(entry) {
      this.tableWidth = entry.contentRect.width;
    },
    columnWidth(key) {
      return Math.floor(COLUMN_WIDTHS[key] * this.widthScale);
    },
    setSortConfig() {
      this.sortConfig = { [this.sortParam]: this.sortOrder };
    },
    isDateColumn(key) {
      return ['last_activity_at', 'created_at'].includes(key);
    },
    getSortOptions(key) {
      if (this.isDateColumn(key)) {
        return [
          {
            value: 'asc',
            label: this.$t(
              'CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.OLDEST_FIRST'
            ),
          },
          {
            value: 'desc',
            label: this.$t(
              'CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.NEWEST_FIRST'
            ),
          },
        ];
      }
      return [
        {
          value: 'asc',
          label: this.$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.A_TO_Z'),
        },
        {
          value: 'desc',
          label: this.$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.Z_TO_A'),
        },
      ];
    },
    getCurrentSort(key) {
      return this.sortConfig[key] || '';
    },
    toggleDropdown(key) {
      this.activeDropdownColumn =
        this.activeDropdownColumn === key ? null : key;
    },
    closeDropdown() {
      this.activeDropdownColumn = null;
    },
    applySortFromDropdown(key, order) {
      this.sortConfig = { [key]: order };
      this.$emit('on-sort-change', { [key]: order });
      this.closeDropdown();
    },
    isFilterColumn(key) {
      return key === 'phone_number' || key === 'email';
    },
    getShowSort(key) {
      return !this.isFilterColumn(key);
    },
    getFilterOptions(key) {
      const all = {
        value: 'all',
        label: this.$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.ALL'),
      };
      if (key === 'phone_number') {
        return [
          all,
          {
            value: 'has',
            label: this.$t(
              'CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.HAS_PHONE_NUMBER'
            ),
          },
          {
            value: 'none',
            label: this.$t(
              'CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.NO_PHONE_NUMBER'
            ),
          },
        ];
      }
      if (key === 'email') {
        return [
          all,
          {
            value: 'has',
            label: this.$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.HAS_EMAIL'),
          },
          {
            value: 'none',
            label: this.$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.NO_EMAIL'),
          },
        ];
      }
      return [];
    },
    getSelectedFilters(key) {
      return [this.columnFilters[key]?.presence || 'all'];
    },
    getColumnQuery(key) {
      return this.columnFilters[key]?.value || '';
    },
    isColumnFiltered(key) {
      return (
        buildColumnFilterItems({ [key]: this.columnFilters[key] }).length > 0
      );
    },
    emitColumnFilter(key, change) {
      this.$emit('on-column-filter', { key, ...change });
    },
    getInitials(name) {
      const displayName = name || 'AR';
      return displayName.slice(0, 2).toUpperCase();
    },
  },
};
</script>

<style lang="scss" scoped>
.contacts-table-wrap {
  .contacts-name-td {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }

  .row--user-block {
    display: flex;
    align-items: center;
    gap: 12px;

    .avatar-badge {
      width: 32px;
      height: 32px;
      border-radius: 9999px;
      background: #dbeafe;
      color: #1d4ed8;
      font-size: 14px;
      line-height: 32px;
      text-align: center;
      font-weight: 400;
      flex-shrink: 0;
    }

    .user-name {
      margin: 0;
      font-size: 14px;
      line-height: 18px;
      color: theme('colors.primary');
      text-transform: none;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  .contact-email-link {
    color: theme('colors.blue.500');
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
    max-width: 100%;
    vertical-align: top;
  }

  ::v-deep .table-v2 tbody tr {
    border-bottom: none;
    box-shadow: none;

    &:nth-child(even) {
      background: theme('colors.gray.50');
    }
  }
}
</style>
